import * as THREE from 'three'
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import type { JapanTerrain } from './shared/JapanTerrain'
import type { WoodChips } from './shared/WoodChips'
import type { SiteMarkers } from './shared/SiteMarkers'
import type { Sfx } from './shared/Sfx'
import type { ViewRig } from './shared/ViewRig'
import type { AerialTiles } from './shared/AerialTiles'
import type { TatamiDeck } from './shared/TatamiDeck'
import type { NarratorLite } from './shared/NarratorLite'

export type WorldMode = 'map' | 'void'

export interface ProtoCtx {
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  controls: OrbitControls
  renderer: THREE.WebGLRenderer
  terrain: JapanTerrain
  markers: SiteMarkers
  chips: WoodChips
  sfx: Sfx
  /** 視点抽象化（PC=カメラ直/XR=リグ移動）。プロトはcameraを直接動かさないこと */
  view: ViewRig
  /** 航空写真タイル（Proto10用・遅延ロード） */
  aerial: AerialTiles
  /** 畳デッキ（リグ子。XR時は常時表示、Proto10はPC でも表示） */
  deck: TatamiDeck
  /** 武四郎アバター（リグ随伴。Proto12では図鑑へ移動） */
  narrator: NarratorLite
  setWorld(mode: WorldMode): void
  setCaption(text: string): void
  setLedger(visible: boolean): void
  stampLedger(no: number): void
  resetLedger(): void
}

/** 各演出プロトの共通IF。begin→(update…)→end。endで自グループを破棄する */
export abstract class ProtoBase {
  abstract readonly title: string
  abstract readonly desc: string
  protected group = new THREE.Group()
  protected elapsed = 0

  constructor(protected ctx: ProtoCtx) {}

  begin(): void {
    this.elapsed = 0
    this.ctx.scene.add(this.group)
    this.start()
  }

  protected abstract start(): void

  /** 毎フレーム。dt=秒 */
  update(_dt: number): void {}

  end(): void {
    this.onDispose()
    this.ctx.scene.remove(this.group)
    disposeDeep(this.group)
  }

  protected onDispose(): void {}
}

export function disposeDeep(root: THREE.Object3D): void {
  root.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.geometry) m.geometry.dispose()
    const mat = (m as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose())
    else mat?.dispose()
  })
  root.clear()
}

export const easeOutCubic = (x: number) => 1 - Math.pow(1 - Math.min(1, Math.max(0, x)), 3)
export const easeInOutCubic = (x: number) => {
  x = Math.min(1, Math.max(0, x))
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
}
